import {
  Component,
  Event,
  EventEmitter,
  Host,
  Listen,
  State,
  h,
} from '@stencil/core';
import { EventEmitterData } from '@stencil/core/internal';
import { Unsubscribe, store } from '@stencil/redux';
import { Constants, Language, NotificationType } from '../../enums/const-enums';
import { switchLanguage, uiReady } from '../../redux/actions/config-actions';
import {
  setEmailAddress,
  setIsEmailAddressRemembered,
} from '../../redux/actions/email-action';
import {
  clearNotification,
  showNotification,
} from '../../redux/actions/notification-action';
import { showOverlay } from '../../redux/actions/overlay-actions';
import { RootState, initialStore } from '../../redux/store';
import { EotpService } from '../../service/eotp-service';
import {
  focusSignElement,
  getCookieValue,
  postMessageToParent,
  setCookie,
} from '../../utils/utils';
import {
  RassEotpErrorCodes,
  RassEotpTranslations,
  rassEotpTranslations,
} from './i18n/rass-eotp.i18n';

@Component({
  tag: 'rass-eotp',
  styleUrl: 'rass-eotp.scss',
  shadow: false,
})
export class RassEotp {
  @State() assetsPath: string;
  @State() language: Language;
  @State() emailAddress: string;
  @State() isEmailAddressRemembered: boolean;
  @State() isEmailAddressInvalidShown: boolean;
  @State() isChallengeRequested = false;
  @State() isChallengeRequestPending = false;
  @State() isSubmitRequestPending = false;
  @Event({ eventName: 'code:reset' }) resetCodeEvent: EventEmitter;
  showNotification!: typeof showNotification;
  clearNotification!: typeof clearNotification;
  showOverlay!: typeof showOverlay;
  switchLanguage!: typeof switchLanguage;
  setEmailAddress!: typeof setEmailAddress;
  setIsEmailAddressRemembered!: typeof setIsEmailAddressRemembered;
  uiReady!: typeof uiReady;
  unsubscribe!: Unsubscribe;

  componentWillLoad(): void {
    store.setStore(initialStore);
    store.mapDispatchToProps(this, {
      showNotification,
      clearNotification,
      showOverlay,
      switchLanguage,
      setEmailAddress,
      setIsEmailAddressRemembered,
      uiReady,
    });
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      const {
        config: { assetsPath, language },
        email: { emailAddress, isEmailAddressRemembered },
      } = state;
      return { assetsPath, language, emailAddress, isEmailAddressRemembered };
    });
    if (getCookieValue(Constants.EMAIL_COOKIE)) {
      this.setIsEmailAddressRemembered(true);
    }
    this.isEmailAddressInvalidShown = false;
    this.clearNotification();
    const language =
      getCookieValue(Constants.LANGUAGE_COOKIE_NAME) || this.language;
    this.switchLanguage(language);
  }

  componentDidLoad(): void {
    focusSignElement('email-input', { preventScroll: true });
    postMessageToParent<AppModel.SignStatusEvent>({
      type: 'sign-status',
      value: 'initialized',
    });
    this.uiReady();
  }

  handleChangeRememberMe(): void {
    this.setIsEmailAddressRemembered(!this.isEmailAddressRemembered);
  }

  @Listen('rass:email:submit')
  getChallenge(event: CustomEvent<string>): void {
    this.isChallengeRequestPending = true;
    this.clearNotification();
    this.setEmailAddress(event.detail);
    new EotpService()
      .doRequestEotp(this.emailAddress)
      .then(() => {
        this.isChallengeRequestPending = false;
        this.isChallengeRequested = true;
      })
      .catch((error: AppModel.HttpResponseErrorJson) => {
        this.handleErrorResponse(error);
        this.isChallengeRequestPending = false;
        this.isChallengeRequested = false;
      });
  }

  cancelChallenge(): void {
    this.isChallengeRequested = false;
    focusSignElement('email-input');
  }

  @Listen('rass:password:submit')
  onCodeSubmit(event: EventEmitterData<string>): void {
    this.isSubmitRequestPending = true;
    this.clearNotification();
    if (this.isEmailAddressRemembered) {
      setCookie(Constants.EMAIL_COOKIE, this.emailAddress);
    } else {
      setCookie(Constants.EMAIL_COOKIE, '');
    }
    new EotpService()
      .doVerifyEotp(this.emailAddress, event.detail)
      .catch((error: AppModel.HttpResponseErrorJson) => {
        this.handleErrorResponse(error);
        this.isSubmitRequestPending = false;
        this.resetCodeEvent.emit();
        focusSignElement('sign_code input');
      });
  }

  handleErrorResponse(error: AppModel.HttpResponseErrorJson): void {
    const faultCode: RassEotpErrorCodes =
      (error.faultCode as RassEotpErrorCodes) || '999';
    const faultText: string =
      this.translations.errors[faultCode] || this.translations.errors['999'];
    if (error.faultText) {
      this.showNotification({
        type: error.faultLevel || NotificationType.ERROR,
        header: '',
        body: faultText + (faultCode ? ` (${faultCode})` : ''),
      });
    }
    if (error.fatal) {
      this.showOverlay({
        message: faultText + (faultCode ? ` (${faultCode})` : ''),
        url: error.fatal.continue_url,
        content: error.fatal.continue_content,
        type: 'error',
        active: true,
      });
    } else if (error.timeout) {
      this.showNotification({
        type: NotificationType.WARNING,
        header: '',
        body: this.translations.errors.timeout,
      });
    }
  }

  get instructions(): string {
    return this.isChallengeRequested
      ? this.translations.instructions.submit + this.emailAddress
      : this.translations.instructions.request;
  }

  get translations(): RassEotpTranslations {
    return rassEotpTranslations[this.language] || rassEotpTranslations.en;
  }

  render(): void {
    return (
      <Host class="section-eotp">
        <style>@import url({`'${this.assetsPath}css/rds-style.css'`});</style>
        <div class="rds-container body-width" role="form">
          <div
            class={
              this.isChallengeRequested
                ? 'rds-display-none'
                : 'rds-row rds-justify-content-center'
            }
          >
            <div class="input-div rds-col-12 rds-margin-block-end-100">
              <rass-email
                language={this.language}
                is-blocking-user-input={this.isChallengeRequestPending}
              />
            </div>
          </div>

          <div
            class={
              this.isChallengeRequested
                ? 'rds-display-none'
                : 'rds-row rds-justify-content-center'
            }
          >
            <div class="rds-col-12">
              <rds-checkbox
                id="email-remember-checkbox"
                checked={this.isEmailAddressRemembered}
                label={this.translations.checkbox.label}
                onRdsChange={(): void => this.handleChangeRememberMe()}
              ></rds-checkbox>
            </div>
          </div>

          <div
            class={
              this.isChallengeRequested
                ? 'rds-row rds-justify-content-center'
                : 'rds-display-none'
            }
          >
            <div class="input-div rds-col-12 rds-margin-block-end-100">
              <rass-password
                language={this.language}
                is-one-time-password={true}
                is-blocking-user-input={
                  !this.isChallengeRequested || this.isSubmitRequestPending
                }
              />
            </div>
          </div>

          <div class="rds-row rds-justify-content-center">
            <div
              class={
                this.isChallengeRequested
                  ? 'rds-col-12 rds-margin-block-end-150'
                  : 'rds-display-none'
              }
            >
              <a
                id="email-cancel"
                class="rds-link rds-text-link--inline cursor-pointer"
                role="button"
                tabindex={0}
                onClick={(): void => this.cancelChallenge()}
                onKeyDown={(event: KeyboardEvent): void => {
                  const target = event.currentTarget as HTMLElement;
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    target.click();
                  }
                }}
              >
                {this.translations.button.label.cancel}
              </a>
            </div>
          </div>

          <div class="rds-row rds-justify-content-center rds-padding-block-end-200">
            <div class="rds-col-12">
              <p
                id="challenge-instructions"
                class="rds-paragraph rds-font-text-align-start"
              >
                <ul class="rds-unordered-list rds-padding-inline-start-150">
                  <li class="rds-text--size-md">{this.instructions}</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </Host>
    ) as void;
  }
}
